import { useEffect, useRef, useState } from "react";
import { Controller, useFieldArray, useFormContext } from "react-hook-form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Info, Plus, Trash2, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CalendarDatePicker } from "@/components/CalendarDatePicker";
import { SKILLS_LIST } from "@/utils/constant";
import { cn } from "@/lib/utils";

export const FreelancerSkillsForm = () => {
  const {
    register,
    formState: { errors },
    setValue,
    control,
    getValues,
    watch,
    setError,
    clearErrors,
  } = useFormContext();

  const skills = watch("skills") || [];
  const newSkill = watch("newSkill") || "";
  const [isSuggestionOpen, setIsSuggestionOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);


  const filteredSkills = SKILLS_LIST.filter((skill) =>
    skill.toLowerCase().includes(newSkill.toLowerCase())
  );

  useEffect(() => {
    if (newSkill.length > 0 && filteredSkills.length > 0) {
      setIsSuggestionOpen(true);
      setHighlightedIndex(-1); // Reset highlight when suggestions change
    } else {
      setIsSuggestionOpen(false);
      setHighlightedIndex(-1);
    }
  }, [newSkill, filteredSkills.length]);

  const onAddSkill = (newSkill: string) => {
    if (newSkill.trim() && !skills.includes(newSkill)) {
      const newSkills = newSkill
        .split(",")
        .map((skill) => skill.trim())
        .filter((skill) => skill.length > 0 && !skills.includes(skill));

      if (newSkills.length > 0) {
        setValue("skills", [...skills, ...newSkills]);
      }
    }
  };

  const handleAddSkill = (skill: string = newSkill) => {
    if (skill.trim() && !skills.includes(skill.trim())) {
      onAddSkill(skill.trim());
      setValue("newSkill", "");
      setIsSuggestionOpen(false);
      setHighlightedIndex(-1);
      inputRef.current?.focus();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (highlightedIndex >= 0 && filteredSkills[highlightedIndex]) {
        handleAddSkill(filteredSkills[highlightedIndex]);
      } else {
        handleAddSkill();
      }
    } else if (e.key === "Escape") {
      setIsSuggestionOpen(false);
      setHighlightedIndex(-1);
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      if (isSuggestionOpen && filteredSkills.length > 0) {
        setHighlightedIndex((prev) =>
          prev < filteredSkills.length - 1 ? prev + 1 : 0
        );
      }
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      if (isSuggestionOpen && filteredSkills.length > 0) {
        setHighlightedIndex((prev) =>
          prev > 0 ? prev - 1 : filteredSkills.length - 1
        );
      }
    }
  };

  const urlRegex = /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/;
  const handleRemoveSkill = (skill: string) => {
    setValue(
      "skills",
      skills.filter((s: string) => s !== skill)
    );
  };

  // Field arrays for certifications, achievements, and socialMediaLinks
  const {
    fields: certificationFields,
    append: appendCertification,
    remove: removeCertification,
  } = useFieldArray({
    control,
    name: "certifications",
  });

  const {
    fields: achievementFields,
    append: appendAchievement,
    remove: removeAchievement,
  } = useFieldArray({
    control,
    name: "achievements",
  });

  const {
    fields: socialMediaFields,
    append: appendSocialMedia,
    remove: removeSocialMedia,
  } = useFieldArray({
    control,
    name: "socialMediaLinksArray",
  });

  const {
    fields: educationFields,
    append: appendEducation,
    remove: removeEducation,
  } = useFieldArray({
    control,
    name: "education",
  });

  const validateSocialMediaLinks = () => {
    const socialMediaArray = getValues("socialMediaLinksArray") || [];
    const socialMediaLinks = socialMediaArray
      .map((item) => item.value)
      .filter((value) => value && urlRegex.test(value)); // Only include valid URLs
    setValue("socialMediaLinks", socialMediaLinks, { shouldValidate: true });
  };

  const handleAddSocialMediaLink = () => {
    const socialMediaArray = getValues("socialMediaLinksArray") || [];
    const hasInvalid = socialMediaArray.some(
      (item) => item.value && !urlRegex.test(item.value)
    );
    if (hasInvalid) {
      socialMediaArray.forEach((item, index) => {
        if (item.value && !urlRegex.test(item.value)) {
          setError(`socialMediaLinksArray.${index}.value`, {
            message: "Invalid URL format",
          });
        }
      });
      return;
    }
    appendSocialMedia({ value: "" });
    validateSocialMediaLinks();
  };

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <div className="flex items-center">
          <Label htmlFor="skills" className="font-medium text-sm text-gray-700">
            Skills
          </Label>
          <div className="relative group ml-2">
            <Info className="h-4 w-4 text-gray-400" />
            <div className="hidden group-hover:block absolute -top-1 left-6 p-2 w-60 text-xs text-gray-600 bg-white rounded shadow-md z-10">
              Add relevant skills you possess.
            </div>
          </div>
        </div>
        <div>
          <Label className="block text-sm font-medium text-gray-700 mb-2">
            Required Skills <span className="text-red-600">*</span>
          </Label>
          <div className="relative">
            <div className="flex gap-2">
              <Input
                ref={inputRef}
                placeholder="Add required skills"
                {...register("newSkill")}
                onKeyDown={handleKeyDown}
                className="border-blue-100 focus:border-emerald-300 focus:ring-1 focus:ring-emerald-300"
              />
              <Button
                onClick={() => handleAddSkill()}
                className="bg-gradient-to-r from-sky-500 to-emerald-500 hover:from-sky-600 hover:to-emerald-600"
              >
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            {isSuggestionOpen && (
              <div
                className={cn(
                  "absolute z-[1000] mt-1 w-full max-h-[200px] overflow-y-auto bg-white border border-gray-200 rounded-md shadow-lg",
                  "text-sm"
                )}
              >
                <ul role="listbox" className="py-1">
                  {filteredSkills.map((skill, index) => (
                    <li
                      key={skill}
                      role="option"
                      onClick={() => handleAddSkill(skill)}
                      className={cn(
                        "px-3 py-2 cursor-pointer hover:bg-accent hover:text-accent-foreground",
                        index === highlightedIndex && "bg-accent text-accent-foreground",
                        "truncate"
                      )}
                      aria-selected={index === highlightedIndex}
                    >
                      {skill}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
        <div className="flex flex-wrap gap-2 mt-2">
          {skills.map((skill: string) => (
            <Badge
              key={skill}
              variant="secondary"
              className="bg-sky-100 text-sky-700 flex items-center gap-1"
            >
              {skill}
              <X
                className="w-3 h-3 cursor-pointer"
                onClick={() => handleRemoveSkill(skill)}
              />
            </Badge>
          ))}
        </div>
        {errors.skills && (
          <p className="text-xs text-red-500">
            {errors.skills.message as string}
          </p>
        )}
      </div>

      {/* Certifications */}
      <div className="space-y-2">
        <Label className="font-medium text-sm text-gray-700">
          Certifications
        </Label>
        {certificationFields.map((field, index) => (
          <div key={field.id} className="flex items-center gap-2">
            <Input
              placeholder="e.g., AWS Certified Developer"
              {...register(`certifications.${index}`)}
              className="border-blue-100 focus:border-emerald-300 focus:ring-1 focus:ring-emerald-300"
            />
            {certificationFields.length > 1 && (
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={() => removeCertification(index)}
                className="border-red-100 text-red-500 hover:bg-red-50"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
          </div>
        ))}
        <Button
          type="button"
          variant="outline"
          onClick={() => appendCertification("")}
          className="mt-2 ms-2 border-blue-100 text-blue-500 hover:bg-blue-50"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Certification
        </Button>
        {errors.certifications && (
          <p className="text-xs text-red-500">
            {errors.certifications.message as string}
          </p>
        )}
      </div>

      {/* Achievements */}
      {/* <div className="space-y-2">
        <Label className="font-medium text-sm text-gray-700">Achievements</Label>
        {achievementFields.map((field, index) => (
          <div key={field.id} className="flex items-center gap-2">
            <Input
              placeholder="e.g., Won Hackathon 2023"
              {...register(`achievements.${index}`)}
              className="border-blue-100 focus:border-emerald-300 focus:ring-1 focus:ring-emerald-300"
            />
            {achievementFields.length > 1 && (
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={() => removeAchievement(index)}
                className="border-red-100 text-red-500 hover:bg-red-50"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
          </div>
        ))}
        <Button
          type="button"
          variant="outline"
          onClick={() => appendAchievement("")}
          className="mt-2 ms-2 border-blue-100 text-blue-500 hover:bg-blue-50"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Achievement
        </Button>
        {errors.achievements && (
          <p className="text-xs text-red-500">{errors.achievements.message as string}</p>
        )}
      </div> */}

      {/* Education */}
      <div className="space-y-2">
        <div className="flex items-center">
          <Label className="font-medium text-sm text-gray-700">Education</Label>
          <div className="relative group ml-2">
            <Info className="h-4 w-4 text-gray-400" />
            <div className="hidden group-hover:block absolute -top-1 left-6 p-2 w-60 text-xs text-gray-600 bg-white rounded shadow-md z-10">
              Add your educational background, including degree, institution,
              and year of graduation. (Optional)
            </div>
          </div>
        </div>

        {educationFields.map((field, index) => (
          <div key={field.id} className="border p-4 rounded-md space-y-4">
            <div className="space-y-2">
              <Label htmlFor={`education.${index}.degree`}>Degree <span className="text-red-600">*</span></Label>
              <Input
                id={`education.${index}.degree`}
                placeholder="e.g., B.Sc. in Computer Science"
                {...register(`education.${index}.degree`)}
                className="border-blue-100 focus:border-emerald-300 focus:ring-1 focus:ring-emerald-300"
              />
              {errors.education?.[index]?.degree && (
                <p className="text-xs text-red-500">
                  {errors.education[index].degree.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor={`education.${index}.institution`}>
                Institution <span className="text-red-600">*</span>
              </Label>
              <Input
                id={`education.${index}.institution`}
                placeholder="e.g., University of California"
                {...register(`education.${index}.institution`)}
                className="border-blue-100 focus:border-emerald-300 focus:ring-1 focus:ring-emerald-300"
              />
              {errors.education?.[index]?.institution && (
                <p className="text-xs text-red-500">
                  {errors.education[index].institution.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor={`education.${index}.year`}>Passing Year <span className="text-red-600">*</span></Label>
              <Controller
                control={control}
                name={`education.${index}.year`}
                render={({ field }) => (
                  <CalendarDatePicker
                    date={field.value || ""}
                    setDate={(value) => field.onChange(value)}
                  />
                )}
              />
              {errors.education?.[index]?.year && (
                <p className="text-xs text-red-500">
                  {errors.education?.[index]?.year?.message || "Please provide a valid date."}
                </p>
              )}
            </div>

            <Button
              type="button"
              variant="destructive"
              onClick={() => removeEducation(index)}
            >
              <Trash2 className="h-3 w-3" />
            </Button>
          </div>
        ))}

        <Button
          type="button"
          onClick={() =>
            appendEducation({
              degree: "",
              institution: "",
              year: "",
            })
          }
        >
          Add Education
        </Button>
      </div>

      {/* Social Media Links */}
      <div className="space-y-2">
        <Label className="font-medium text-sm text-gray-700">
          Social Media Links
        </Label>
        {socialMediaFields.map((field, index) => (
          <div key={field.id} className="flex items-center gap-2">
            <div className="flex-1">
              <Input
                placeholder="URL (e.g., https://linkedin.com/in/username)"
                {...register(`socialMediaLinksArray.${index}.value`, {
                  onChange: () => {
                    clearErrors(`socialMediaLinksArray.${index}.value`);
                    validateSocialMediaLinks();
                  },
                })}
                className="border-blue-100 focus:border-emerald-300 focus:ring-1 focus:ring-emerald-300"
              />
              {errors.socialMediaLinksArray?.[index]?.value && (
                <p className="text-xs text-red-500">
                  {errors.socialMediaLinksArray[index].value.message}
                </p>
              )}
            </div>
            {socialMediaFields.length > 0 && (
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={() => {
                  removeSocialMedia(index);
                  validateSocialMediaLinks();
                }}
                className="border-red-100 text-red-500 hover:bg-red-50"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
          </div>
        ))}
        <Button
          type="button"
          variant="outline"
          onClick={handleAddSocialMediaLink}
          className="mt-2 ms-2 border-blue-100 text-blue-500 hover:bg-blue-50"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Social Media Link
        </Button>
        {errors.socialMediaLinks && (
          <p className="text-xs text-red-500">
            {errors.socialMediaLinks.message as string}
          </p>
        )}
      </div>
    </div>
  );
};
